import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Row, Spin, Tag, Typography, Table } from 'antd';
import { ArrowLeftOutlined, DollarOutlined } from '@ant-design/icons';
import LandingLayout from './LandingLayout';
import { ThemeContext } from '../../context/ThemeContext';
import './BorrowingDetails.css';
import apiClient from '../../services/apiClient';

const { Title, Text } = Typography;

const BorrowingDetails = () => {
  const navigate = useNavigate();
  const { issuerName } = useParams();
  const { isDark } = useContext(ThemeContext);
  const [borrowings, setBorrowings] = useState([]);
  const [borrowingDetails, setBorrowingDetails] = useState({});
  const [ncds, setNcds] = useState({ secured: [], unsecured: [] });
  const [commercialPapers, setCommercialPapers] = useState({ secured: [], unsecured: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
  const decodedIssuerName = issuerName ? decodeURIComponent(issuerName) : '';

  const formatBorrowingValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') {
      return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    }
    return value;
  };

  const borrowingFieldDefinitions = [
    { key: 'termLoanFromBanks', label: 'Term Loan From Banks' },
    { key: 'termLoanFromOtherBanks', label: 'Term Loan From Other Banks' },
    { key: 'termLoanFromFinancialInstitutions', label: 'Term Loan From Financial Institutions' },
    { key: 'termLoanFromForeignBanks', label: 'Term Loan From Foreign Banks' },
    { key: 'termLoanFromForeignFinancialInstitutions', label: 'Term Loan From Foreign Financial Institutions' },
    { key: 'externalCommercialBorrowingsEcb', label: 'External Commercial Borrowings (ECB)' },
    { key: 'otherLoans', label: 'Other Loans' },
    { key: 'termLoanFromNhb', label: 'Term Loan From NHB' },
    { key: 'nonConvertibleDebenturesNcds', label: 'Non-Convertible Debentures (NCDs)' },
    { key: 'loansRepayableOnDemandWorkingCapitalDemandLoan', label: 'Loans Repayable on Demand (Working Capital/Demand Loan)' },
    { key: 'loansFromRelatedParties', label: 'Loans From Related Parties' },
    { key: 'cashCreditFromBanks', label: 'Cash Credit From Banks' },
    { key: 'bankOverdraft', label: 'Bank Overdraft' },
    { key: 'borrowingsWithinIndia', label: 'Borrowings Within India' },
    { key: 'borrowingsOutsideIndia', label: 'Borrowings Outside India' }
  ];

  const borrowingDetailsFieldDefinitions = [
    { key: 'repaymentDetails', label: 'Repayment Details' },
    { key: 'rateOfInterest', label: 'Rate Of Interest' },
    { key: 'tenure', label: 'Tenure' },
    { key: 'totalAmount', label: 'Total Amount' }
  ];

  const ncdsFieldDefinitions = [
    { key: 'seriesDetails', label: 'Series Details' },
    { key: 'dateOfAllotment', label: 'Date Of Allotment' },
    { key: 'faceValuePerDebenture', label: 'Face Value Per Debenture' },
    { key: 'amount', label: 'Amount' },
    { key: 'interestRate', label: 'Interest Rate' },
    { key: 'dateOfRedemption', label: 'Date Of Redemption' },
    { key: 'redeemableTerms', label: 'Redeemable Terms' },
    { key: 'numberOfNcds', label: 'Number Of NCDs' }
  ];

  const commercialPapersFieldDefinitions = [
    { key: 'seriesDetails', label: 'Series Details' },
    { key: 'dateOfAllotment', label: 'Date Of Allotment' },
    { key: 'faceValuePerPaper', label: 'Face Value Per Paper' },
    { key: 'amount', label: 'Amount' },
    { key: 'interestRate', label: 'Interest Rate' },
    { key: 'dateOfRedemption', label: 'Date Of Redemption' },
    { key: 'redeemableTerms', label: 'Redeemable Terms' }
  ];

  const getBorrowingSecurityType = (borrowing) => {
    return borrowing?.borrowingSecurityType || borrowing?.id?.borrowingSecurityType || 'UNKNOWN';
  };

  const renderBorrowingFields = (borrowing, fieldDefinitions, options = {}) => {
    const { hideEmpty = false } = options;
    const fieldsToRender = hideEmpty
      ? fieldDefinitions.filter(field => borrowing?.[field.key] !== null && borrowing?.[field.key] !== undefined)
      : fieldDefinitions;

    if (fieldsToRender.length === 0) {
      return <Text type="secondary">No borrowing values available</Text>;
    }

    return fieldsToRender.map((field) => (
      <div key={field.key} className="borrowing-field">
        <span className="borrowing-field-label">{field.label}</span>
        <span className="borrowing-field-value">{formatBorrowingValue(borrowing?.[field.key])}</span>
      </div>
    ));
  };

  const formatBorrowingTypeLabel = (value) => {
    if (!value) return 'Borrowing Details';
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const buildTableColumns = (fieldDefinitions) => {
    return fieldDefinitions.map((field) => ({
      title: field.label,
      dataIndex: field.key,
      key: field.key,
      render: (value) => formatBorrowingValue(value)
    }));
  };

  const fetchBorrowings = async () => {
    if (!decodedIssuerName) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${baseUrl}/borrowing/issuer/${encodeURIComponent(decodedIssuerName)}`, {
        validateStatus: () => true,
      });
      if (response.status === 204 || response.status === 404) {
        setBorrowings([]);
        return;
      }
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;
      if (result.statusCode === 200 && result?.data) {
        setBorrowings(Array.isArray(result.data.borrowings) ? result.data.borrowings : []);
        setBorrowingDetails(result.data.borrowingDetails && typeof result.data.borrowingDetails === 'object'
          ? result.data.borrowingDetails
          : {});
        setNcds({
          secured: Array.isArray(result?.data?.ncds?.secured) ? result.data.ncds.secured : [],
          unsecured: Array.isArray(result?.data?.ncds?.unsecured) ? result.data.ncds.unsecured : []
        });
        setCommercialPapers({
          secured: Array.isArray(result?.data?.commercialPapers?.secured) ? result.data.commercialPapers.secured : [],
          unsecured: Array.isArray(result?.data?.commercialPapers?.unsecured) ? result.data.commercialPapers.unsecured : []
        });
      } else {
        setBorrowings([]);
        setBorrowingDetails({});
        setNcds({ secured: [], unsecured: [] });
        setCommercialPapers({ secured: [], unsecured: [] });
      }
    } catch (err) {
      console.error('Error fetching borrowings:', err);
      setBorrowings([]);
      setBorrowingDetails({});
      setNcds({ secured: [], unsecured: [] });
      setCommercialPapers({ secured: [], unsecured: [] });
      setError('Error fetching borrowing details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBorrowings();
  }, [decodedIssuerName]);

  const securedBorrowings = borrowings.filter(
    borrowing => getBorrowingSecurityType(borrowing) === 'SECURED'
  );
  const unsecuredBorrowings = borrowings.filter(
    borrowing => getBorrowingSecurityType(borrowing) === 'UNSECURED'
  );

  return (
    <LandingLayout>
      <div className={`borrowings-page ${isDark ? 'dark' : ''}`}>
        <div className="borrowings-page-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            className="borrowings-back-button"
          >
            Back
          </Button>
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Borrowing Details
            </Title>
            <Text type="secondary">{decodedIssuerName || 'Issuer'}</Text>
          </div>
        </div>

        <Card
          className="borrowings-page-card"
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarOutlined style={{ color: '#13c2c2' }} />
              <span>Borrowings</span>
              <Tag color="cyan">{borrowings.length}</Tag>
            </span>
          }
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spin size="large" />
            </div>
          ) : error ? (
            <Alert message={error} type="error" showIcon />
          ) : (
            <div className="borrowings-details-sections">
              <div className="borrowings-detail-section">
                <div className="borrowings-section-title">
                  <Title level={5} style={{ margin: 0 }}>Borrowings</Title>
                  <Tag color="cyan">{borrowings.length}</Tag>
                </div>
                {borrowings.length > 0 ? (
                  <Row gutter={[16, 16]} className="borrowings-grid">
                    <Col xs={24} md={12}>
                      <div className="borrowings-group">
                        <div className="borrowings-group-header">
                          <Tag color="green">Secured ({securedBorrowings.length})</Tag>
                        </div>
                        {securedBorrowings.length > 0 ? (
                          securedBorrowings.map((borrowing, index) => (
                            <div key={`secured-${index}`} className="borrowing-card">
                              {renderBorrowingFields(borrowing, borrowingFieldDefinitions, { hideEmpty: true })}
                            </div>
                          ))
                        ) : (
                          <Text type="secondary">No secured borrowings found</Text>
                        )}
                      </div>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className="borrowings-group">
                        <div className="borrowings-group-header">
                          <Tag color="orange">Unsecured ({unsecuredBorrowings.length})</Tag>
                        </div>
                        {unsecuredBorrowings.length > 0 ? (
                          unsecuredBorrowings.map((borrowing, index) => (
                            <div key={`unsecured-${index}`} className="borrowing-card">
                              {renderBorrowingFields(borrowing, borrowingFieldDefinitions, { hideEmpty: true })}
                            </div>
                          ))
                        ) : (
                          <Text type="secondary">No unsecured borrowings found</Text>
                        )}
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <Text type="secondary">No borrowings found</Text>
                )}
              </div>

              <div className="borrowings-detail-section">
                <div className="borrowings-section-title">
                  <Title level={5} style={{ margin: 0 }}>Borrowing Details</Title>
                  <Tag color="blue">
                    {Object.values(borrowingDetails || {}).reduce(
                      (total, group) => total + (group?.secured?.length || 0) + (group?.unsecured?.length || 0),
                      0
                    )}
                  </Tag>
                </div>
                {Object.keys(borrowingDetails || {}).length > 0 ? (
                  Object.entries(borrowingDetails || {}).map(([typeKey, group]) => {
                    const securedCount = group?.secured?.length || 0;
                    const unsecuredCount = group?.unsecured?.length || 0;
                    if (securedCount + unsecuredCount === 0) {
                      return null;
                    }
                    return (
                      <div key={typeKey} className="borrowings-detail-subsection">
                        <div className="borrowings-subsection-title">
                          <Title level={5} style={{ margin: 0 }}>
                            {formatBorrowingTypeLabel(typeKey)}
                          </Title>
                        </div>
                        <Row gutter={[16, 16]}>
                          {securedCount > 0 && (
                            <Col xs={24}>
                              <div className="borrowings-group">
                                <div className="borrowings-group-header">
                                  <Tag color="green">Secured ({securedCount})</Tag>
                                </div>
                                <Row gutter={[16, 16]}>
                                  {group.secured.map((detail, index) => (
                                    <Col xs={24} md={12} key={`detail-${typeKey}-secured-${index}`}>
                                      <div className="borrowing-card">
                                        {renderBorrowingFields(detail, borrowingDetailsFieldDefinitions, { hideEmpty: true })}
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            </Col>
                          )}
                          {unsecuredCount > 0 && (
                            <Col xs={24}>
                              <div className="borrowings-group">
                                <div className="borrowings-group-header">
                                  <Tag color="orange">Unsecured ({unsecuredCount})</Tag>
                                </div>
                                <Row gutter={[16, 16]}>
                                  {group.unsecured.map((detail, index) => (
                                    <Col xs={24} md={12} key={`detail-${typeKey}-unsecured-${index}`}>
                                      <div className="borrowing-card">
                                        {renderBorrowingFields(detail, borrowingDetailsFieldDefinitions, { hideEmpty: true })}
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    );
                  })
                ) : (
                  <Text type="secondary">No borrowing details found</Text>
                )}
              </div>

              <div className="borrowings-detail-section">
                <div className="borrowings-section-title">
                  <Title level={5} style={{ margin: 0 }}>NCDs</Title>
                  <Tag color="purple">{(ncds.secured?.length || 0) + (ncds.unsecured?.length || 0)}</Tag>
                </div>
                {((ncds.secured?.length || 0) + (ncds.unsecured?.length || 0)) > 0 ? (
                  <Row gutter={[16, 16]}>
                    {ncds.secured?.length > 0 && (
                      <Col xs={24}>
                        <div className="borrowings-table-card">
                          <Tag color="green">Secured ({ncds.secured?.length || 0})</Tag>
                          <Table
                            columns={buildTableColumns(ncdsFieldDefinitions)}
                            dataSource={ncds.secured}
                            pagination={{ pageSize: 10 }}
                            size="small"
                            rowKey={(record, index) => `ncd-secured-${index}`}
                          />
                        </div>
                      </Col>
                    )}
                    {ncds.unsecured?.length > 0 && (
                      <Col xs={24}>
                        <div className="borrowings-table-card">
                          <Tag color="orange">Unsecured ({ncds.unsecured?.length || 0})</Tag>
                          <Table
                            columns={buildTableColumns(ncdsFieldDefinitions)}
                            dataSource={ncds.unsecured}
                            pagination={{ pageSize: 10 }}
                            size="small"
                            rowKey={(record, index) => `ncd-unsecured-${index}`}
                          />
                        </div>
                      </Col>
                    )}
                  </Row>
                ) : (
                  <Text type="secondary">No NCDs found</Text>
                )}
              </div>

              <div className="borrowings-detail-section">
                <div className="borrowings-section-title">
                  <Title level={5} style={{ margin: 0 }}>Commercial Papers</Title>
                  <Tag color="geekblue">{(commercialPapers.secured?.length || 0) + (commercialPapers.unsecured?.length || 0)}</Tag>
                </div>
                {((commercialPapers.secured?.length || 0) + (commercialPapers.unsecured?.length || 0)) > 0 ? (
                  <Row gutter={[16, 16]}>
                    {commercialPapers.secured?.length > 0 && (
                      <Col xs={24}>
                        <div className="borrowings-table-card">
                          <Tag color="green">Secured ({commercialPapers.secured?.length || 0})</Tag>
                          <Table
                            columns={buildTableColumns(commercialPapersFieldDefinitions)}
                            dataSource={commercialPapers.secured}
                            pagination={{ pageSize: 10 }}
                            size="small"
                            rowKey={(record, index) => `cp-secured-${index}`}
                          />
                        </div>
                      </Col>
                    )}
                    {commercialPapers.unsecured?.length > 0 && (
                      <Col xs={24}>
                        <div className="borrowings-table-card">
                          <Tag color="orange">Unsecured ({commercialPapers.unsecured?.length || 0})</Tag>
                          <Table
                            columns={buildTableColumns(commercialPapersFieldDefinitions)}
                            dataSource={commercialPapers.unsecured}
                            pagination={{ pageSize: 10 }}
                            size="small"
                            rowKey={(record, index) => `cp-unsecured-${index}`}
                          />
                        </div>
                      </Col>
                    )}
                  </Row>
                ) : (
                  <Text type="secondary">No commercial papers found</Text>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </LandingLayout>
  );
};

export default BorrowingDetails;

